/**
 * FilterBar component
 * Main UI for managing filters: displays active filters, add filter button, result count.
 */

import { HTMLAttributes } from 'react';

import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import styles from './FilterBar.module.scss';
import { AddFilterMenu } from '../AddFilterMenu/AddFilterMenu';
import { InlineFilterEdit } from '../InlineFilterEdit/InlineFilterEdit';
import type { FilterSet, FilterClause } from '@/lib/filter-dsl';
import type { ResourceConfig } from '@/lib/resource-config';

interface FilterBarProps extends HTMLAttributes<HTMLDivElement> {
  config: ResourceConfig;
  filterSet: FilterSet;
  onAddFilter: (clause: FilterClause) => void;
  onRemoveFilter: (facetKey: string) => void;
  onUpdateFilter: (clause: FilterClause) => void;
  /**
   * Optional search query to filter facet counts.
   * Facet counts will show how many items match both the filters AND the search.
   *
   * Note: We chose to pass searchQuery as a simple string prop rather than accessing
   * it from the FilterViewStore. While the store has searchQuery available, prop
   * drilling provides:
   * - Clearer intent (explicit dependency on search)
   * - Simpler types (string vs UseBoundStore)
   * - Less coupling to store implementation
   *
   * If we need to pass multiple search-related values in future (e.g., searchMode,
   * searchFields), consider using the store approach to avoid excessive prop drilling.
   */
  searchQuery?: string;
  // View management props
  currentView?: { filterViewId: string; name: string } | null;
  hasUnsavedChanges?: boolean;
  hasFilters?: boolean;
  isSaving?: boolean;
  isTransitioning?: boolean;
  onSaveCurrentView?: () => void;
  onSaveAsNewView?: () => void;
  onResetFilters?: () => void;
}

export const FilterBar = ({
  config,
  filterSet,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  searchQuery,
  currentView,
  hasUnsavedChanges,
  hasFilters,
  isSaving,
  isTransitioning,
  onSaveCurrentView,
  onSaveAsNewView,
  onResetFilters,
  ...props
}: FilterBarProps) => {
  const { t } = useTranslation('views');

  return (
    <Box className={styles.FilterBar} {...props}>
      <Stack
        direction="row"
        gap={2}
        alignItems="flex-start"
        flexWrap="wrap"
        justifyContent="space-between"
      >
        {/* Active inline filter editors */}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {filterSet.map((clause) => (
            <InlineFilterEdit
              key={clause.facet}
              clause={clause}
              config={config}
              filterSet={filterSet}
              searchQuery={searchQuery}
              onDelete={() => onRemoveFilter(clause.facet)}
              onUpdate={(updatedClause) => onUpdateFilter(updatedClause)}
            />
          ))}

          <AddFilterMenu
            config={config}
            filterSet={filterSet}
            searchQuery={searchQuery}
            onAddFilter={onAddFilter}
          />
        </Stack>

        {/* View action buttons - hide during view transition to prevent flash */}
        {/* Show buttons when: hasFilters OR (currentView with unsaved changes) */}
        {(hasFilters || (currentView && hasUnsavedChanges)) &&
          !isTransitioning && (
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              {/* Cancel/Reset button - show when there are unsaved changes or unsaved filters */}
              {((currentView && hasUnsavedChanges) ||
                (!currentView && hasFilters)) &&
                onResetFilters && (
                  <Button
                    data-automation-id="filter-bar-reset-filters-button"
                    variant="text"
                    onClick={onResetFilters}
                    size="tiny"
                    color="secondary"
                  >
                    {currentView ? t('Cancel') : t('ClearFilters')}
                  </Button>
                )}

              {/* Save as new view - show when there are unsaved changes OR no current view with filters */}
              {(hasUnsavedChanges || (!currentView && hasFilters)) &&
                onSaveAsNewView && (
                  <Button
                    data-automation-id="filter-bar-save-as-new-view-button"
                    variant="outlined"
                    onClick={onSaveAsNewView}
                    size="tiny"
                    color="secondary"
                    disabled={isSaving}
                  >
                    {t('SaveAsNewView')}
                  </Button>
                )}

              {/* Update current view - show when current view has unsaved changes */}
              {currentView && hasUnsavedChanges && onSaveCurrentView && (
                <Button
                  data-automation-id="filter-bar-update-view-button"
                  variant="contained"
                  color="secondary"
                  onClick={onSaveCurrentView}
                  size="tiny"
                  disabled={isSaving}
                >
                  {isSaving ? t('Saving') : t('UpdateView')}
                </Button>
              )}
            </Stack>
          )}
      </Stack>
    </Box>
  );
};
